import React, { useState, useEffect } from "react";
import { Modal, Select, Table, Tag, message } from "antd";
import { organizationAPI } from "../../../../api/organizationApi";
import { fetchUsers } from "../../../../api/userManagement";

const isUnassigned = (name) => name === "待分配组织";

const BatchTransferModal = ({
  visible,
  mode,
  selectedUserIds,
  selectedOrg,
  organizationFlatList,
  onCancel,
  onSuccess,
  canUpdate,
}) => {
  const [targetOrgId, setTargetOrgId] = useState(null);
  const [defaultOrgId, setDefaultOrgId] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) {
      setTargetOrgId(null);
      setDefaultOrgId(null);
      setUsers([]);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchUsers({ page: 1, page_size: 500 });
        const all = res.items || [];
        setUsers(all.filter((u) => selectedUserIds.includes(u.id)));
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [visible, selectedUserIds]);

  const handleOk = async () => {
    if (!canUpdate) return;
    try {
      setLoading(true);
      if (mode === "transfer") {
        if (!targetOrgId || !selectedOrg) return;
        for (const userId of selectedUserIds) {
          await organizationAPI.assignUserToOrganization(targetOrgId, userId);
          if (!isUnassigned(selectedOrg.name)) {
            await organizationAPI.removeUserFromOrganization(
              selectedOrg.id,
              userId
            );
          }
        }
        message.success(`已切换 ${selectedUserIds.length} 人到目标组织`);
      } else if (mode === "remove") {
        if (!selectedOrg) return;
        for (const userId of selectedUserIds) {
          await organizationAPI.removeUserFromOrganization(
            selectedOrg.id,
            userId
          );
        }
        message.success(`已移除 ${selectedUserIds.length} 人`);
      } else if (mode === "setDefault") {
        if (!defaultOrgId) return;
        for (const userId of selectedUserIds) {
          await organizationAPI.setUserDefaultOrganization(userId, defaultOrgId);
        }
        message.success(`已设置 ${selectedUserIds.length} 人的默认组织`);
      }
      onSuccess();
    } catch (err) {
      message.error(err.response?.data?.detail || "操作失败");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "用户名", dataIndex: "username", key: "username" },
    {
      title: "姓名",
      dataIndex: "full_name",
      key: "full_name",
      render: (v) => v || "-",
    },
  ];

  const titleMap = {
    transfer: "批量换组织",
    remove: "批量移出组织",
    setDefault: "批量设默认组织",
  };

  return (
    <Modal
      title={titleMap[mode] || "批量操作"}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      okButtonProps={{
        disabled:
          (mode === "transfer" && !targetOrgId) ||
          (mode === "setDefault" && !defaultOrgId),
      }}
    >
      <div style={{ marginBottom: 16 }}>
        已选择 <Tag color="blue">{selectedUserIds.length}</Tag> 个用户
      </div>
      {mode === "transfer" && (
        <div style={{ marginBottom: 16 }}>
          <div>目标组织：</div>
          <Select
            showSearch
            style={{ width: "100%", marginTop: 8 }}
            placeholder="选择目标组织"
            value={targetOrgId}
            onChange={setTargetOrgId}
            options={organizationFlatList
              .filter(
                (o) => o.id !== selectedOrg?.id && !isUnassigned(o.name)
              )
              .map((o) => ({ value: o.id, label: o.pathLabel || o.name }))}
            optionFilterProp="label"
          />
        </div>
      )}
      {mode === "setDefault" && (
        <div style={{ marginBottom: 16 }}>
          <div>默认组织：</div>
          <Select
            showSearch
            style={{ width: "100%", marginTop: 8 }}
            placeholder="选择默认组织"
            value={defaultOrgId}
            onChange={setDefaultOrgId}
            options={organizationFlatList
              .filter((o) => !isUnassigned(o.name))
              .map((o) => ({ value: o.id, label: o.pathLabel || o.name }))}
            optionFilterProp="label"
          />
        </div>
      )}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        size="small"
        pagination={false}
      />
    </Modal>
  );
};

export default BatchTransferModal;
